import { createApi } from '@reduxjs/toolkit/query/react'
import { MANAGE_SUPPORT_STAFF_LIST, MANAGE_SUPPORT_STAFF_ADD, MANAGE_SUPPORT_STAFF_VIEW, MANAGE_SUPPORT_STAFF_EDIT, MANAGE_SUPPORT_STAFF_DELETE, MANAGE_SUPPORT_STAFF_ASSOCIATE_CLIENT_DELETE, RESEND_ACCOUNT_ACTIVATION_MAIL, MANAGE_SUPPORT_STAFF_THERAPIST_DELETE, MANAGE_SUPPORT_STAFF_ASSIGN_THERAPIST, MANAGE_SUPPORT_STAFF_CLIENT_LIST, MANAGE_SUPPORT_STAFF_CLIENT_TASK_SESSION_VIEW, MANAGE_SUPPORT_STAFF_ASSOCIATED_THERAPISTS_LIST, MANAGE_SUPPORT_STAFF_ASSOCIATED_CLIENTS_LIST, MANAGE_SUPPORT_STAFF_STATUS_EDIT, MANAGE_SUPPORT_STAFF_GROUPS_LIST, MANAGE_SUPPORT_STAFF_GROUP_TASKS_LIST, MANAGE_SUPPORT_STAFF_GROUP_SESSION_VIEW, MANAGE_SUPPORT_STAFF_ADDV2, MANAGE_SUPPORT_STAFF_EDITV2, MANAGE_SUPPORT_STAFF_ASSOCIATED_THERAPISTS_LIST_V2 } from "config/apiPaths"
import axiosBaseQuery from "utils/axios/axiosBaseQuery"

export const supportStaffApi = createApi({

    reducerPath: 'supportStaffApi',
    baseQuery: axiosBaseQuery({
        baseUrl: "/support-staff",
    }),
    tagTypes: ["SupportStaffList", "SupportStaffDetails"],
    endpoints: (builder) => ({

        // get support staff list
        getSupportStaffList: builder.query({
            query: ({ search = "", client_type = "", status = "", school = "", division = "", page = 1, limit = 10, sort = { column: "id", direction: "desc" } }) => ({
                method: "PATCH",
                url: MANAGE_SUPPORT_STAFF_LIST,
                data: { search, status: status?.value, client_type: client_type?.value, page, limit, sort, school: school?.value, division: division?.value },
            }),
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["SupportStaffList"]
        }),

        getSupportStaffClientTasksList: builder.query({
            query: ({ search = "", status = "", page = 1, limit = 10, sort = { column: "id", direction: "desc" }, staffId, clientId, task_type }) => ({
                method: "PATCH",
                url: `${MANAGE_SUPPORT_STAFF_CLIENT_LIST}/${staffId}/${clientId}`,
                data: { search, status, page, limit, sort, task_type },
            }),
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["SupportStaffClientTasksList"]
        }),

        getSupportStaffClientTaskSessionView: builder.query({
            query: ({ id, clientId, taskId, payload }) => {
                return ({
                    method: "POST",
                    url: `${MANAGE_SUPPORT_STAFF_CLIENT_TASK_SESSION_VIEW}/${id}/${clientId}/${taskId}`,
                    data: payload
                })
            },
            transformResponse: (response) => {
                return response.data;
            },
        }),

        // Associated Therapists List
        getStaffAssociatedTherapistList: builder.query({
            query: ({ search = "", status = "", page = 1, limit = 10, id }) => ({
                method: "PATCH",
                url: `${MANAGE_SUPPORT_STAFF_ASSOCIATED_THERAPISTS_LIST}/${id}`,
                data: {
                    search,
                    status,
                    page,
                    limit
                }
            }),
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["StaffAssociatedTherapistList"]
        }),

        // Associated Therapists List V2
        getStaffAssociatedTherapistListV2: builder.query({
            query: ({ search = "", status = "", page = 1, limit = 10, id }) => ({
                method: "PATCH",
                url: `${MANAGE_SUPPORT_STAFF_ASSOCIATED_THERAPISTS_LIST_V2}/${id}`,
                data: {
                    search,
                    status,
                    page,
                    limit
                }
            }),
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["StaffAssociatedTherapistList"]
        }),

        // Associated Clients List
        getStaffAssociatedClientstList: builder.query({
            query: ({ search = "", status = "", page = 1, limit = 10, id }) => ({
                method: "PATCH",
                url: `${MANAGE_SUPPORT_STAFF_ASSOCIATED_CLIENTS_LIST}/${id}`,
                data: {
                    search,
                    status,
                    page,
                    limit
                }
            }),
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["StaffAssociatedClientstList"]
        }),

        // get support staff data
        getSupportStaffDetail: builder.query({
            query: (id) => ({
                method: "GET",
                url: `${MANAGE_SUPPORT_STAFF_VIEW}/${id}`
            }),
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["SupportStaffDetails"]
        }),

        // create support staff
        createSupportStaff: builder.mutation({
            query: (data) => ({
                method: "POST",
                url: MANAGE_SUPPORT_STAFF_ADD,
                data
            }),
            invalidatesTags: ["SupportStaffList", "StaffAssociatedTherapistList", "StaffAssociatedClientstList"]
        }),

        // create support staff V2
        createSupportStaffV2: builder.mutation({
            query: (data) => ({
                method: "POST",
                url: MANAGE_SUPPORT_STAFF_ADDV2,
                data
            }),
            invalidatesTags: ["SupportStaffList", "StaffAssociatedTherapistList", "StaffAssociatedClientstList"]
        }),

        // update support staff
        updateSupportStaff: builder.mutation({
            query: ({ data, staffId }) => ({
                method: "PUT",
                url: `${MANAGE_SUPPORT_STAFF_EDIT}/${staffId}`,
                data
            }),
            invalidatesTags: ["SupportStaffList", "SupportStaffDetails", "StaffAssociatedTherapistList", "StaffAssociatedClientstList"]
        }),

        // update support staffV2
        updateSupportStaffV2: builder.mutation({
            query: ({ data, staffId }) => ({
                method: "PUT",
                url: `${MANAGE_SUPPORT_STAFF_EDITV2}/${staffId}`,
                data
            }),
            invalidatesTags: ["SupportStaffList", "SupportStaffDetails", "StaffAssociatedTherapistList", "StaffAssociatedClientstList"]
        }),

        updateSupportStaffStatus: builder.mutation({
            query: ({ data, staffId }) => ({
                method: "PUT",
                url: `${MANAGE_SUPPORT_STAFF_STATUS_EDIT}/${staffId}`,
                data
            }),
            invalidatesTags: ["SupportStaffList", "SupportStaffDetails", "StaffAssociatedTherapistList", "StaffAssociatedClientstList"]
        }),

        // delete support staff
        deleteSupportStaff: builder.mutation({
            query: (id) => ({
                method: "DELETE",
                url: `${MANAGE_SUPPORT_STAFF_DELETE}/${id}`
            }),
            invalidatesTags: ["SupportStaffList", "SupportStaffDetails"]
        }),

        // delete associated client
        deleteAssociatedCLient: builder.mutation({
            query: (id) => ({
                method: "DELETE",
                url: `${MANAGE_SUPPORT_STAFF_ASSOCIATE_CLIENT_DELETE}/${id}`
            }),
            invalidatesTags: ["SupportStaffDetails"]
        }),

        deleteAssociatedTherapist: builder.mutation({
            query: ({ id }) => ({
                method: "DELETE",
                url: `${MANAGE_SUPPORT_STAFF_THERAPIST_DELETE}/${id}`
            }),
            invalidatesTags: ["SupportStaffList", "SupportStaffDetails", "StaffAssociatedTherapistList", "StaffAssociatedTherapistList"]
        }),

        assignAssociatedTherapist: builder.mutation({
            query: (data) => ({
                method: "POST",
                url: MANAGE_SUPPORT_STAFF_ASSIGN_THERAPIST,
                data
            }),
            invalidatesTags: ["SupportStaffList", "SupportStaffDetails", "StaffAssociatedTherapistList", "StaffAssociatedClientstList"]
        }),

        // resend account activation mail
        resendStaffAccountActivation: builder.mutation({
            query: (id) => ({
                method: "PUT",
                url: `${RESEND_ACCOUNT_ACTIVATION_MAIL}/${id}`
            })
        }),

        getTaskDetailsStaff: builder.query({
            query: ({ id, clientId, taskId, taskType }) => {
                return ({
                    method: "GET",
                    url: `/staff/taskview/${id}/${clientId}/${taskId}`
                })
            },
            transformResponse: (response) => {
                return response.data;
            },
            providesTags: ["TaskStatusStaff"],
        }),

        getStaffGroupsList: builder.query({
            query: ({ id, search, page, limit, sort }) => ({
                method: "PATCH",
                url: `${MANAGE_SUPPORT_STAFF_GROUPS_LIST}/${id}`,
                data: { search, page, limit, sort }
            }),

            transformResponse: (response) => {
                return response.data;
            },
        }),

        getStaffGroupTasksList: builder.query({
            query: ({ staffId, groupId, payload = { search: "", page: 1, limit: 10, sort: { column: "id", direction: "ASC" } } }) => ({
                method: "GET",
                url: `${MANAGE_SUPPORT_STAFF_GROUP_TASKS_LIST}/${staffId}/${groupId}`,
                data: payload
            }),

            transformResponse: (response) => {
                return response.data;
            },
        }),

        getStaffGroupSessionView: builder.query({
            query: ({ staffId, groupId, task_datetime }) => ({
                method: "PATCH",
                url: `${MANAGE_SUPPORT_STAFF_GROUP_SESSION_VIEW}/${staffId}/${groupId}`,
                data: { task_datetime }
            }),

            transformResponse: (response) => {
                return response.data;
            },
        }),
    }),
})

export const {

    useCreateSupportStaffMutation,
    useDeleteSupportStaffMutation,
    useGetSupportStaffDetailQuery,
    useGetSupportStaffListQuery,
    useUpdateSupportStaffMutation,
    useDeleteAssociatedCLientMutation,
    useDeleteAssociatedTherapistMutation,
    useResendStaffAccountActivationMutation,
    useAssignAssociatedTherapistMutation,
    useGetSupportStaffClientTasksListQuery,
    useLazyGetTaskDetailsStaffQuery,
    useLazyGetSupportStaffClientTaskSessionViewQuery,
    useGetStaffAssociatedTherapistListQuery,
    useGetStaffAssociatedClientstListQuery,
    useUpdateSupportStaffStatusMutation,
    useGetStaffGroupsListQuery,
    useGetStaffGroupTasksListQuery,
    useGetStaffGroupSessionViewQuery,
    useCreateSupportStaffV2Mutation,
    useUpdateSupportStaffV2Mutation,
    useGetStaffAssociatedTherapistListV2Query
} = supportStaffApi;